import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { initPushNotifications, initAppEvents, isNative } from "@/lib/native";

/**
 * Bootstrapper that lives inside <BrowserRouter> so it can access `useNavigate`.
 * On native shells (Capacitor iOS/Android):
 *   1. Wires deep-link (Universal / App Link) handling.
 *   2. Requests push permission + registers the APNs/FCM token with the
 *      backend once the user is authenticated.
 */
export const NativeBootstrap: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Deep links — once per app lifecycle.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await initAppEvents((path) => navigate(path));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push — only after user is signed in.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      if (!(await isNative())) return;
      if (cancelled) return;
      await initPushNotifications(async ({ platform, push_token }) => {
        try {
          await api.post("/devices/register", { platform, push_token });
        } catch {
          /* logged in native.ts */
        }
      });
    })();
    return () => { cancelled = true; };
  }, [user]);

  return null;
};

export default NativeBootstrap;
